import { memo, useEffect, useState } from "react";
import { useStore } from "../store/globalState";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useTranslation } from "react-i18next";
import {
  usePreventReload,
  useKeyboardVisible,
} from "lola-framework-ui-test/src/hooks";
import {
  AuraLayout,
  Button,
  type IVGSCardInfo,
  Layout,
  VgsFormWrapper,
  VgsInput,
} from "lola-framework-ui-test";
import { APP_ROUTES, flavorClassName, TITLE_COLOR } from "../constants";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { loadVgsConfig } from "../utils/tokenization-config";
import {
  CARD_BRANDS,
  DEFAULT_ALLOWED_BRANDS,
  type CardBrandKey,
} from "../constants/card-brands";
import { paymentType } from "../interfaces/payment";
import { useSessionStore } from "../store/useSessionStore";
import { addCard } from "../services/api";
import { useNavigate } from "react-router-dom";
import { useClaimLink } from "../hooks/useClaimLink";
import { useVirtualKeyboardOpen } from "../hooks/useVirtualKeyboardOpen";
import { trackScreenView } from "../analytics";
import { Navbar } from "../components/Navbar";

const vgsCardInfoInitialState: IVGSCardInfo = {
  bin: "",
  cardType: "",
  last4: "",
};

export type IVGSFields = {
  card_holder_name?: string;
  card_number?: string;
  card_exp_date?: string;
  card_cvc?: string;
};

export const AddCardCollectPage = memo(() => {
  const {
    cardState,
    setCardState,
    theme,
    setPaymentState,
    cardList,
    setCardList,
  } = useStore();
  useClaimLink();
  const { session } = useSessionStore();
  const navigate = useNavigate();
  const { redirectTo } = useRouteWithToken();

  useEffect(() => {
    trackScreenView("add_card_collect");
  }, []);
  const { t } = useTranslation();

  const [errorMessage, setErrorMessage] = useState<IVGSFields>({
    card_holder_name: "",
    card_number: "",
    card_exp_date: "",
    card_cvc: "",
  });
  const [collectAttempt, setCollectAttempt] = useState(0);
  const [collectStatus, setCollectStatus] = useState<
    "loading" | "ready" | "error"
  >("loading");
  const [collectConfig, setCollectConfig] = useState<{
    vaultId: string;
    environment: "sandbox" | "live";
    routeId: string;
    version?: string;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setCollectStatus("loading");
    setCollectConfig(null);
    loadVgsConfig()
      .then((vgs) => {
        if (cancelled) return;
        const routeId = vgs.collect?.routeId?.trim();
        if (!routeId) {
          setCollectStatus("error");
          return;
        }
        setCollectConfig({
          vaultId: vgs.vaultId,
          environment: vgs.environment,
          routeId,
          version: vgs.collect?.version,
        });
        setCollectStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setCollectStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [collectAttempt]);

  const { viewportHeight } = useKeyboardVisible();
  const keyboardOpen = useVirtualKeyboardOpen();
  const [vgsCardInfo, setVgsCardInfo] = useState<IVGSCardInfo>(
    vgsCardInfoInitialState
  );

  usePreventReload();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFormSubmitSuccess = async (status: any, data: any) => {
    if (status === 200 && data) {
      const cardNumber = data.card_number;
      const last4 = vgsCardInfo.last4;
      const brand = vgsCardInfo.cardType.toLocaleUpperCase();
      const name = `${brand}-${last4}`;
      const [expiryMonth, rawExpiryYear] = data.card_exp_date.split('/');
      const expiryYear = rawExpiryYear.trim().length === 2 ? `20${rawExpiryYear.trim()}` : rawExpiryYear.trim();

      try {
        // Sync card with backend
        const linkId = session.data?.linkId;
        if (!linkId) {
          console.error("No linkId found in session");
          return;
        }

        const backendCard = await addCard(linkId, {
          token: cardNumber, // VGS token
          last4,
          brand,
          expiryMonth: expiryMonth.trim(),
          expiryYear: expiryYear.trim(),
          cardholderName: data.card_holder_name,
        });

        // Store card with backend cardId
        const cardData = {
          cardId: backendCard.cardId,
          name,
          brand,
          last4: last4,
          accountNumber: cardNumber,
          expirationDate: data.card_exp_date,
          fullName: data.card_holder_name,
          cvv: data.card_cvc,
        };

        setCardState({
          ...cardState,
          ...cardData,
        });

        setPaymentState({
          payment: {
            cardId: backendCard.cardId,
            type: paymentType.CARD,
            brand: name,
          },
        });

        const cardExists = cardList.find((card) => card.cardId === backendCard.cardId);
        if (!cardExists) setCardList([...cardList, cardData]);

        setErrorMessage({
          card_holder_name: "",
          card_number: "",
          card_exp_date: "",
          card_cvc: "",
        });

        redirectTo(APP_ROUTES.TRANSFER_PAGE);
      } catch (error) {
        console.error("Failed to sync card with backend:", error);
        // TODO: Show error to user
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onHandlerError = (error: any) => {
    const messages: IVGSFields = {};
    Object.keys(error).forEach((errorField: string) => {
      const message = error[errorField]?.errorMessages;
      messages[errorField as keyof IVGSFields] = message.join(",\n");
    });
    setErrorMessage(messages);
  };
  const colorsConfig = theme?.whiteView;

  const { backgroundBtn, textColorBtn, bodyCopy } = colorsConfig;

  const allowedBrands = (session.data?.config?.cardPolicy?.allowedCardBrands ?? DEFAULT_ALLOWED_BRANDS) as CardBrandKey[];
  const isUSOnly =
    (session.data?.config?.cardPolicy?.cardOriginPolicy ?? "us_only") ===
    "us_only";

  const brandsLabel = allowedBrands
    .map((key) => CARD_BRANDS[key]?.label)
    .filter(Boolean)
    .join(", ");

  const activeColor = backgroundBtn;
  const inputColor = bodyCopy;
  const inactiveColor = colorsConfig.subtitile?.includes("gradient(")
    ? "#979797"
    : colorsConfig.subtitile;
  const userStatus = session.data?.userStatus;
  const canSend = !userStatus?.blocked && !userStatus?.suspended;

  if (!canSend) {
    redirectTo(APP_ROUTES.TRANSACTIONS_PAGE, { replace: true });
    return null;
  }

  return (
    <AuraLayout
      colorConfig={colorsConfig}
      className={`white-view-background ${flavorClassName}`}
    >
      <Layout.Header>
        <Navbar
          align="center"
          color={TITLE_COLOR}
          title={isUSOnly ? t("card.addUS") : t("card.add")}
          onBackClick={() => navigate(-1)}
        />
      </Layout.Header>

      <Layout.Content
        className="collect collect-vgs"
        style={{ height: `${viewportHeight}px` }}
      >
        <ScrollFadeIndicator color={bodyCopy} />
        {collectStatus === "ready" && collectConfig ? (
        <VgsFormWrapper
          vaultId={collectConfig.vaultId}
          environment={collectConfig.environment}
          routeId={collectConfig.routeId}
          {...(collectConfig.version ? { version: collectConfig.version } : {})}
          action="/post"
          submitParameters={{}}
          onSubmitCallback={handleFormSubmitSuccess}
          onErrorCallback={onHandlerError}
        >
          <section className="mock-card-fields">
            <div className="mock-card-field">
              <VgsInput
                autoFocus
                type="card_holder_name"
                placeholder={`${t("card.owner")} *`}
                errorLabel={errorMessage.card_holder_name}
                activeColor={activeColor}
                inactiveColor={inactiveColor}
                errorColor={inputColor}
                color={inputColor}
              />
            </div>

            <div className="mock-card-field">
              <VgsInput
                type="card_number"
                placeholder={`${t("card.number")} *`}
                errorLabel={errorMessage.card_number}
                activeColor={activeColor}
                inactiveColor={inactiveColor}
                errorColor={inputColor}
                color={inputColor}
                cardNumberFormPlaceholder={`${t("card.number")} *`}
                onGetCardInfo={setVgsCardInfo}
              />
            </div>

            <section className="vgs-inline">
              <div className="mock-card-field" style={{ flex: 1 }}>
                <VgsInput
                  type="card_exp_date"
                  placeholder={`${t("card.expiry")} *`}
                  errorLabel={errorMessage.card_exp_date}
                  activeColor={activeColor}
                  inactiveColor={inactiveColor}
                  errorColor={inputColor}
                  color={inputColor}
                />
              </div>
              <div className="mock-card-field" style={{ flex: 1 }}>
                <VgsInput
                  type="card_cvc"
                  placeholder={`${t("card.cvvPlaceholder")} *`}
                  errorLabel={errorMessage.card_cvc}
                  activeColor={activeColor}
                  inactiveColor={inactiveColor}
                  errorColor={inputColor}
                  color={inputColor}
                />
              </div>
            </section>
          </section>
          <section className="valid-cards">
            {!keyboardOpen && (
              <section className="valid-cards-container">
                <section className="valid-cards-logos">
                  {allowedBrands.map((key) => {
                    const brand = CARD_BRANDS[key];
                    if (!brand) return null;
                    return (
                      <img
                        key={key}
                        className="card-logos"
                        src={brand.logo}
                        alt={t(brand.altKey)}
                      />
                    );
                  })}
                </section>
                <p className="footer-text foreground">
                  {t("card.acceptedBrands", { brands: brandsLabel })}
                </p>
              </section>
            )}
            <section className="homepage-footer">
              <Button
                type="submit"
                size="small"
                color={textColorBtn}
                background={backgroundBtn}
              >
                {t("card.continue")}
              </Button>
            </section>
          </section>
        </VgsFormWrapper>
        ) : (
          <section className="homepage-footer">
            <p className="footer-text foreground" style={{ color: bodyCopy }}>
              {collectStatus === "error"
                ? t("vgs.configUnavailable")
                : t("loading")}
            </p>
            {collectStatus === "error" && (
              <Button
                type="button"
                size="small"
                color={textColorBtn}
                background={backgroundBtn}
                onClick={() => setCollectAttempt((attempt) => attempt + 1)}
              >
                {t("common.retry")}
              </Button>
            )}
          </section>
        )}
      </Layout.Content>
    </AuraLayout>
  );
});
